import { Check, Undo2 } from 'lucide-react';
import { motion } from 'motion/react';

interface ReviewPillProps {
  onAccept: () => void;
  onRevert?: () => void;
  canRevert: boolean;
  isAccepting: boolean;
  isReverting: boolean;
  /**
   * Pixel offset from the bottom of the viewport. The dock sits at
   * bottom: 16 with ~70px of dock height itself, so 100 puts the buttons
   * just above the dock with a small gap.
   */
  bottomOffset?: number;
}

/**
 * Floating Accept + Revert buttons that sit above the prompt dock when a
 * fresh result is in review. No outer container — the two buttons render
 * flat against the canvas with the same shape as the dock's Generate
 * button. Self-dismisses when the user types in the prompt or paints on
 * the mask — see use-canvas-interaction.ts + the input onChange in
 * description-input.tsx, both of which call dismissReview.
 *
 * Pinned to the viewport (`position: fixed`) so the horizontal center is
 * relative to the screen, not the canvas stage — independent of any
 * scrollbar / padding the parent might introduce.
 */
export function ReviewPill({
  onAccept,
  onRevert,
  canRevert,
  isAccepting,
  isReverting,
  bottomOffset = 100,
}: ReviewPillProps) {
  return (
    <motion.div
      data-testid="review-pill"
      // Pass the -50% horizontal centering shift to motion as a motion
      // value (`x`), not as a CSS transform in `style`. Motion writes its
      // own `transform: ...` string for `y` and clobbers anything we put
      // in `style.transform`, which is what was pulling the pill off the
      // screen centre.
      initial={{ opacity: 0, x: '-50%', y: 8 }}
      animate={{ opacity: 1, x: '-50%', y: 0 }}
      exit={{ opacity: 0, x: '-50%', y: 8 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
      style={{
        position: 'fixed',
        left: '50%',
        bottom: `${String(bottomOffset)}px`,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '10px',
        // No background / border / shadow — the buttons sit flat against
        // the canvas, matching the rest of the floating chrome.
        zIndex: 26,
        pointerEvents: 'none',
      }}
    >
      {canRevert && onRevert && (
        <button
          type="button"
          onClick={onRevert}
          disabled={isReverting || isAccepting}
          aria-label="Revert to the previous image"
          title="Revert to the previous image"
          data-testid="review-pill-revert"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '7px',
            padding: '10px 16px',
            backgroundColor: 'rgba(16, 16, 20, 0.92)',
            color: 'var(--color-text-primary)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '6px',
            fontFamily: 'var(--font-sans)',
            fontSize: '13px',
            fontWeight: 500,
            cursor: isReverting || isAccepting ? 'default' : 'pointer',
            height: '40px',
            minWidth: '120px',
            opacity: isReverting || isAccepting ? 0.5 : 1,
            backdropFilter: 'blur(12px) saturate(160%)',
            WebkitBackdropFilter: 'blur(12px) saturate(160%)',
            boxShadow: '0 12px 36px rgba(0, 0, 0, 0.5)',
            pointerEvents: 'auto',
          }}
        >
          <Undo2 size={14} aria-hidden="true" />
          {isReverting ? 'Reverting…' : 'Revert'}
        </button>
      )}
      <button
        type="button"
        onClick={onAccept}
        disabled={isAccepting}
        aria-label="Accept result (Enter)"
        title="Accept result (Enter)"
        data-testid="review-pill-accept"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '7px',
          padding: '10px 16px',
          backgroundColor: 'var(--color-accent-lime)',
          color: '#0a0a0a',
          border: 0,
          borderRadius: '6px',
          fontFamily: 'var(--font-sans)',
          fontSize: '13px',
          fontWeight: 600,
          cursor: isAccepting ? 'default' : 'pointer',
          height: '40px',
          minWidth: '120px',
          opacity: isAccepting ? 0.5 : 1,
          boxShadow: '0 12px 36px rgba(0, 0, 0, 0.5)',
          pointerEvents: 'auto',
        }}
      >
        <Check size={14} aria-hidden="true" />
        {isAccepting ? 'Accepting…' : 'Accept'}
      </button>
    </motion.div>
  );
}
